import React, { useRef, useEffect, useState } from 'react';
import { compose, withProps } from 'recompose';
import { Marker, GoogleMap, InfoWindow, withScriptjs, withGoogleMap } from 'react-google-maps';
import { MAP_SETTINGS } from './constatnts';
import mapStyles from './mapStyles.json';
import styled from 'styled-components';
import * as S from './map.styles';
import getDateObject from '@stencil/flights-date-utils/src/get-date-object';
import FlightIcon from '../../../../icons/FlightIcon';
import formatPrice from '@ibibo/format-price';

const MapWrapper = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
`;

const MapElement = styled.div`
  .gm-ui-hover-effect {
    display: none !important;
  }
  .gm-style .gm-style-iw-t::after {
    background: black;
    height: 10px;
    width: 10px;
  }
  .gm-style-iw.gm-style-iw-c {
    padding: 0;
    padding: 8px !important;
    background: black;
    .gm-style-iw-d {
      overflow: hidden !important;
    }
  }
  .gmnoprint.gm-bundled-control.gm-bundled-control-on-bottom {
    top: 0;
  }
  .gm-style .gm-style-iw-c {
    border-radius: 0px !important;
  }
`;

const { MARKER_SIZE_X, MARKER_SIZE_Y, DEFAULT_ZOOM, DEFAULT_CENTER, DEFAULT_MAP_OPTIONS } =
  MAP_SETTINGS;

const CityInfoPopup = ({ cityName, price, depDate }) => {
  let dateTobeDisplayed = '';
  if (depDate) {
    const date = new Date(depDate.split(' ')[0]);
    dateTobeDisplayed = getDateObject(date)?.ddmmm;
  }
  return (
    <S.CityInfoWrapper>
      <div className="tooltip">
        <p className="f500 marginB5">{cityName || ''}</p>
        <p>
          <span className="blueCnt f500">
            {' '}
            <FlightIcon width="1.2rem" height="1.0rem" />
            {formatPrice(price) || ''}
          </span>{' '}
          <span className="greyCnt">on</span>{' '}
          <span className="blueCnt f500">{` ${dateTobeDisplayed || ''}`}</span>
        </p>
      </div>
    </S.CityInfoWrapper>
  );
};

const CurveMarker = ({ pos1, pos2, mapProjection, zoom }) => {
  if (!mapProjection) {
    return <div />;
  }
  var curvature = 0.4;
  const p1 = mapProjection.fromLatLngToPoint(pos1),
    p2 = mapProjection.fromLatLngToPoint(pos2);

  // Calculating the arc.
  const e = new window.google.maps.Point(p2.x - p1.x, p2.y - p1.y), // endpoint
    m = new window.google.maps.Point(e.x / 2, e.y / 2), // midpoint
    o = new window.google.maps.Point(e.y, -e.x), // orthogonal
    c = new window.google.maps.Point(m.x + curvature * o.x, m.y + curvature * o.y); //curve control point

  const pathDef = 'M 0,0 ' + 'q ' + c.x + ',' + c.y + ' ' + e.x + ',' + e.y;
  const scale = 1 / Math.pow(2, -(zoom ? zoom : 7));

  const symbol = {
    path: pathDef,
    scale: scale,
    strokeWeight: 2,
    strokeColor: '#1874CD',
    fillColor: 'none'
  };

  return <Marker position={pos1} clickable={false} icon={symbol} zIndex={-2} />;
};

const MapContainer = compose(
  withProps({
    googleMapURL:
      'https://maps.googleapis.com/maps/api/js?v=3.exp&libraries=geometry,drawing,places&key=AIzaSyBWUqfHBf_21rKZGFzlX-4lJP4Dg6a1Hk4',
    loadingElement: <div style={{ height: `100%` }} />,
    containerElement: <div style={{ height: `100%` }} />,
    mapElement: <MapElement style={{ height: `81%` }} />
  }),
  withScriptjs,
  withGoogleMap
)(props => {
  const {
    origins,
    destinations,
    cityRef,
    handleSelectedDestination,
    selectedDestination,
    handleOpenDtlSection,
    setNumberOfDestinations,
    numberOfDestinations
  } = props;
  if (!origins && !destinations && destinations.length === 0) {
    return null;
  }
  const mapRef = useRef(null);

  const [currentSelectedMarker, setCurrentSelectedMarker] = useState(null);
  const [hoverMarker, setHoverMarker] = useState(null);
  const [clickedMarkerLatLong, setClickedMarkerLatLong] = useState(null);
  const [mapProjection, setMapProjection] = useState(null);
  const [zoomValue, setZoomValue] = useState(null);

  useEffect(() => {
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(new window.google.maps.LatLng(origins.lat, origins.lng));
    if (destinations) {
      destinations.forEach(({ lat, lng }) => {
        bounds.extend(new window.google.maps.LatLng(lat, lng));
      });
    }
    console.log();
    mapRef.current.fitBounds(bounds);
  }, [destinations, origins, selectedDestination]);

  useEffect(() => {
    if (destinations && selectedDestination && selectedDestination.toCity) {
      const index = destinations.findIndex(
        destination => destination.toCity === selectedDestination.toCity
      );
      if (index !== -1) {
        setCurrentSelectedMarker(index);
        const { lat, lng } = destinations[index];
        setClickedMarkerLatLong({ lat, lng });
      }
    } else {
      setCurrentSelectedMarker(null);
      setClickedMarkerLatLong(null);
    }
  }, [selectedDestination]);

  const updateSelectedDestination = index => {
    handleSelectedDestination(destinations[index]);
    handleOpenDtlSection(index);
    if (numberOfDestinations < index) {
      setNumberOfDestinations(index + 5);
    }
  };

  return (
    <GoogleMap
      ref={mapRef}
      onProjectionChanged={() => {
        setMapProjection(mapRef.current.getProjection());
      }}
      onZoomChanged={() => {
        setZoomValue(mapRef.current.getZoom());
      }}
      defaultZoom={DEFAULT_ZOOM}
      defaultCenter={DEFAULT_CENTER}
      defaultOptions={{ ...DEFAULT_MAP_OPTIONS, styles: mapStyles }}
    >
      {origins.lat && origins.lng ? (
        <Marker
          key={'origin'}
          position={{ lat: origins.lat, lng: origins.lng }}
          icon={{
            url: 'https://gos3.ibcdn.com/green-marker-1637907129.png'
          }}
        ></Marker>
      ) : null}
      {destinations &&
        destinations.map(({ lat, lng, minCostStop, toCity, depDate }, index) => (
          <Marker
            key={index}
            position={{ lat, lng }}
            icon={{
              url: 'https://gos3.ibcdn.com/citycountry_map_tt-1637950843.png',
              labelOrigin: new window.google.maps.Point(30, 11),
              scaledSize: new window.google.maps.Size(MARKER_SIZE_X, MARKER_SIZE_Y)
            }}
            label={{
              text: formatPrice(minCostStop?.price),
              position: 'absolute',
              fontWeight: 'bold',
              fontFamily: 'Helvetica Neue',
              fontStyle: 'normal',
              fontSize: '12px',
              color: '#FFFFFF',
              lineHeight: '15px',
              textAlign: 'center'
            }}
            onMouseOver={() => {
              setHoverMarker(index);
            }}
            onMouseOut={() => {
              setHoverMarker(null);
            }}
            onClick={() => {
              updateSelectedDestination(index);
            }}
          >
            {(currentSelectedMarker !== null && currentSelectedMarker === index) ||
            (hoverMarker !== null && hoverMarker === index) ? (
              <InfoWindow options={{ pixelOffset: new window.google.maps.Size(0, -5) }}>
                <CityInfoPopup
                  cityName={cityRef[toCity]}
                  price={minCostStop?.price}
                  depDate={minCostStop?.depDate}
                />
              </InfoWindow>
            ) : null}
          </Marker>
        ))}

      {clickedMarkerLatLong ? (
        <CurveMarker
          pos1={new window.google.maps.LatLng(clickedMarkerLatLong.lat, clickedMarkerLatLong.lng)}
          pos2={new window.google.maps.LatLng(origins.lat, origins.lng)}
          mapProjection={mapProjection}
          zoom={zoomValue}
        />
      ) : null}
    </GoogleMap>
  );
});

const MapUI = props => (
  <MapWrapper>
    <MapContainer {...props} />
  </MapWrapper>
);

export default MapUI;
